import React, { Dispatch, useCallback } from 'react';
import { IActionEnum } from '../helpers';
import { useClient } from 'hooks/useClient';
import fp from 'lodash/fp';
import { getSessionTokenV3 } from 'helpers/sessionContext';


/**
 * Async function that will request to the API the account's settings.
 */
export type IUseFetchSettingsFn = ((id: number | string) => Promise<void>)[];

/**
 * Creates a custom hook that fetch and store the domain, account or default
 * settings for the current user.
 * @param {Dispatch<any>} dispatch - reducer dispatch method.
 */
export const useFetchSettings = (dispatch: Dispatch<any>): IUseFetchSettingsFn => {
  const client = useClient({
    url: process.env.REACT_APP_API_V3,
    token: getSessionTokenV3() as string,
  });

  // Track mount status
  const isMounted = React.useRef(true);
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  /**
   * Fetches the global settings.
   */
  const fetchSettings = useCallback(async (id: number | string) => {
    try {
      if (!isMounted.current) return;
      dispatch({ type: IActionEnum.FETCH_SETTINGS });

      const r1 = await client.getSettings();
      // Check again after await
      if (!isMounted.current) return;

      const r2 = await client.getRewardsAvailability(id);
      if (!isMounted.current) return;

      const isRewardsAvailable = fp.compose(
        (v: string) => JSON.parse(v),
        fp.get('value'),
        fp.find({ key: 'rewards' }),
        fp.get('data'),
      )(r1);

      const isUserRewardsAvailable = fp.get('rewards_availability')(r2);

      dispatch({ type: IActionEnum.FETCH_SETTINGS_SUCCESS, isRewardsAvailable, isUserRewardsAvailable });
    } catch (e) {
      if (!isMounted.current) return;
      dispatch({
        type: IActionEnum.FETCH_SETTINGS_FAILURE,
        fallback: 'isRewardsAvailable',
      });
    }
  }, [client, dispatch]);

  /**
   * Fetches the user settings.
   */
  const fetchUserSettings = useCallback(async (id: number | string) => {
    try {
      if (!isMounted.current) return;
      dispatch({ type: IActionEnum.FETCH_SETTINGS });

      const response = await client.getRewardsAvailability(id);
      if (!isMounted.current) return;

      const value = fp.get('rewards_availability')(response);

      dispatch({ type: IActionEnum.FETCH_SETTINGS_SUCCESS, isUserRewardsAvailable: value, isRewardsAvailable: value });
    } catch (e) {
      if (!isMounted.current) return;
      dispatch({ type: IActionEnum.FETCH_SETTINGS_FAILURE, fallback: 'isUserRewardsAvailable' });
    }
  }, [client, dispatch]);

  const fn = useCallback(async (id: number | string) => {
    dispatch({ type: IActionEnum.FETCH_SETTINGS_INIT });
    await fetchSettings(id)
    dispatch({ type: IActionEnum.FETCH_SETTINGS_FINISH });
  }, [dispatch, fetchSettings]);

  return [fn, fetchUserSettings];
};
